import { PageOptions } from '@graphcommerce/framer-next-pages'
import { ProductListQuery } from '@graphcommerce/magento-product'
import { PageMeta, StoreConfigDocument } from '@graphcommerce/magento-store'
import {
  GetStaticProps,
  MetaRobots,
} from '@graphcommerce/next-ui'
import { GetStaticPaths } from 'next'
import { RowRenderer, RowProduct } from '../../components'
import { LayoutFull, LayoutFullProps } from '../../components/Layout/LayoutFull'
import { CategoryListDocument } from '../../graphql/CategoryList.gql'
import { DefaultPageQuery, DefaultPageDocument } from '../../graphql/DefaultPage.gql'
import { ProductListWithReviewsDocument } from '../../graphql/ProductListWithReviews.gql'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'
import { LayoutDocument } from 'components/Layout/Layout.gql'
import { GetCustomReviewsDocument } from 'graphql/GetCustomReviews.gql'
import { RowHeroBanner } from 'components/GraphCMS/RowHeroBanner/RowHeroBanner'
import useProductIDs from 'components/ChipLabCustom/ID/useProductIDs'

type Props = DefaultPageQuery & ProductListQuery
type RouteProps = { url: string }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
export type GetPageStaticProps = GetStaticProps<LayoutFullProps, Props, RouteProps>

function CustomPokerChips(props) {
  const { pages, products, products2, products3, aggregatedReviews } = props
  const page = pages?.[0]

  const { chipIDs } = useProductIDs();

  const title = page?.title ?? ''

  const product = products.products?.items?.[0]
  const metaRobots = page?.metaRobots.toLowerCase().split('_').flat(1) as MetaRobots[]

  function specificRenders(rowProps) {
    if (rowProps.uid) {
      if (rowProps.uid === products.uid) {
        return <RowProduct {...rowProps} {...product} items={products.products?.items} shopByUse={chipIDs} setMainReviewCount={() => { }} />;
      } else if (rowProps.uid === products2?.uid) {
        return <RowProduct {...rowProps} {...products2.products?.items?.[0]} items={products2.products?.items} shopByUse={chipIDs} setMainReviewCount={() => { }} />;
      } else if (rowProps.uid === products3?.uid) {
        return <RowProduct {...rowProps} {...products3.category?.children?.[0]} items={products3.category?.children} shopByUse={chipIDs} setMainReviewCount={() => { }} />;
        // return <RowCategory items={products3.category?.children} />;
      } else {
        return <RowProduct {...rowProps} {...product} items={products.products?.items} shopByUse={chipIDs} setMainReviewCount={() => { }} />;
      }
    }
    return <RowProduct {...rowProps} {...product} items={products.products?.items} shopByUse={chipIDs} setMainReviewCount={() => { }} />;
  }

  return (
    <>
      <PageMeta
        title={page?.metaTitle ?? title ?? ''}
        metaDescription={page?.metaDescription ?? ''}
        metaRobots={metaRobots}
        canonical={page?.url === 'page/home' ? '/' : `/${page?.url}`}
      />

      {pages?.[0] && (
        <RowRenderer
          content={pages?.[0].content}
          renderer={{
            RowProduct: (rowProps) => (
              specificRenders(rowProps)
            ),
            RowHeroBanner: (rowProps) => (
              <RowHeroBanner aggregatedReviews={aggregatedReviews} {...rowProps} />
            ),
          }}
        />
      )}
    </>
  )
}

CustomPokerChips.pageOptions = {
  Layout: LayoutFull,
} as PageOptions

export default CustomPokerChips

export const getStaticProps = async ({ locale, params }) => {
  const urlKey = params?.url ?? '??'
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)

  const conf = client.query({ query: StoreConfigDocument })
  const page = staticClient.query({
    query: DefaultPageDocument,
    variables: {
      url: `custom-poker-chips`,
      rootCategory: (await conf).data.storeConfig?.root_category_uid ?? '',
    },
  })

  // todo(paales): Remove when https://github.com/Urigo/graphql-mesh/issues/1257 is resolved
  const categoryUid = String((await conf).data.storeConfig?.root_category_uid ?? '')
  const eq1 = 'ODE=';
  const eq2 = 'NDU=';
  const eq3 = 'MTIw';
  const productList = staticClient.query({
    query: ProductListWithReviewsDocument,
    variables: { categoryUid, pageSize: 8, filters: { category_uid: { eq: eq1 } } },
  })
  const productList2 = staticClient.query({
    query: ProductListWithReviewsDocument,
    variables: { categoryUid, pageSize: 8, filters: { category_uid: { eq: eq2 } } },
  })
  const productList3 = staticClient.query({
    // query: ProductListDocument,
    query: CategoryListDocument,
    // variables: { categoryUid, pageSize: 8, filters: { category_uid: { eq: eq3 } } },
    variables: { ID: 33 },
  })
  const aggregatedReviews = client.query({
    query: GetCustomReviewsDocument,
    variables: { pageSize: 10, currentPage: 1, productIds: "5056,7622" },
  })

  const layout = staticClient.query({ query: LayoutDocument })


  if (!(await page).data.pages?.[0]) return { notFound: true }

  return {
    props: {
      ...(await page).data,
      ...(await layout).data,
      products: { uid: eq1, ...(await productList).data },
      products2: { uid: eq2, ...(await productList2).data },
      products3: { uid: eq3, ...(await productList3).data },
      aggregatedReviews: (await aggregatedReviews).data,
      up: { href: '/', title: 'Home' },
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: 60 * 20,
  }
}
